import { useEffect, useRef, useState, type ReactNode, type RefObject, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { cx } from "./ui";

export type ResponsiveWorkspacePanelProps = {
  readonly id: string;
  readonly label: string;
  readonly side: "left" | "right";
  readonly open: boolean;
  readonly onClose: () => void;
  readonly staticQuery: string;
  readonly openerRef: RefObject<HTMLButtonElement>;
  readonly children: ReactNode;
  readonly className?: string;
};

const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);
  return matches;
};

const focusableSelector = 'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

export function ResponsiveWorkspacePanel({ id, label, side, open, onClose, staticQuery, openerRef, children, className }: ResponsiveWorkspacePanelProps) {
  const staticVisible = useMediaQuery(staticQuery);
  const modalOpen = open && !staticVisible;
  const visible = staticVisible || open;
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (panelRef.current !== null) panelRef.current.inert = !visible;
  }, [visible]);

  useEffect(() => {
    if (!modalOpen) return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : openerRef.current;
    const focusReturnTarget = openerRef.current ?? previousFocusRef.current;
    const focusClose = () => closeRef.current?.focus();
    const frame = typeof window.requestAnimationFrame === "function" ? window.requestAnimationFrame(focusClose) : window.setTimeout(focusClose, 0);
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      if (typeof window.cancelAnimationFrame === "function") window.cancelAnimationFrame(frame);
      else window.clearTimeout(frame);
      window.removeEventListener("keydown", closeOnEscape);
      focusReturnTarget?.focus();
      previousFocusRef.current = null;
    };
  }, [modalOpen, onClose, openerRef]);

  const containFocus = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (!modalOpen || event.key !== "Tab") return;
    const focusable = [...(panelRef.current?.querySelectorAll<HTMLElement>("*") ?? [])]
      .filter((element) => element.matches(focusableSelector));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) {
      event.preventDefault();
      closeRef.current?.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <>
      {modalOpen && <button type="button" aria-label={`Close ${label}`} onClick={onClose} className="absolute inset-0 z-30 bg-slate-950/35" />}
      <div
        ref={panelRef}
        id={id}
        role={modalOpen ? "dialog" : "complementary"}
        aria-modal={modalOpen ? "true" : undefined}
        aria-label={label}
        aria-hidden={!visible}
        onKeyDown={containFocus}
        className={cx(
          "inset-y-0 z-40 flex min-h-0 shrink-0 flex-col bg-slate-50 transition-transform duration-200",
          side === "left" ? "left-0 w-[min(90vw,390px)] border-r border-slate-300" : "right-0 w-[min(92vw,400px)] border-l border-slate-300",
          staticVisible ? "relative translate-x-0 shadow-none" : "absolute shadow-2xl",
          !staticVisible && side === "left" && (open ? "translate-x-0" : "-translate-x-full invisible"),
          !staticVisible && side === "right" && (open ? "translate-x-0" : "translate-x-full invisible"),
          className,
        )}
      >
        {modalOpen && (
          <button ref={closeRef} type="button" aria-label={`Close ${label}`} onClick={onClose} className="absolute right-3 top-3 z-20 grid h-11 w-11 place-items-center rounded-lg bg-white text-xl text-slate-600 shadow-sm ring-1 ring-slate-300 hover:bg-slate-100">×</button>
        )}
        {children}
      </div>
    </>
  );
}
